import { Extension } from "@tiptap/core"

/**
 * What a callout is for: Mintlify's six callouts, plus `info` and `success`
 * from before. Stored `info` stays the blue Note box; Mintlify's grey Info is
 * `neutral`. The word picks the colour and the icon; the CSS in
 * `packages/ui/src/styles/mintlify/callouts.css` owns both, so the editor and
 * the portal render the same box.
 */
export const CALLOUT_VARIANTS = [
	"note",
	"info",
	"neutral",
	"tip",
	"check",
	"warning",
	"danger",
	"success",
] as const

export type CalloutVariant = (typeof CALLOUT_VARIANTS)[number]

// Every plain <blockquote> already stored rendered as the blue box, which is
// Mintlify's Note, so the default keeps that look with no attribute.
export const DEFAULT_CALLOUT_VARIANT: CalloutVariant = "note"

export function isCalloutVariant(value: unknown): value is CalloutVariant {
	return (
		typeof value === "string" &&
		(CALLOUT_VARIANTS as readonly string[]).includes(value)
	)
}

/** The stored attribute, normalised. Anything unknown reads as the default. */
export function readCalloutVariant(value: unknown): CalloutVariant {
	return isCalloutVariant(value) ? value : DEFAULT_CALLOUT_VARIANT
}

declare module "@tiptap/core" {
	interface Commands<ReturnType> {
		calloutVariant: {
			setCalloutVariant: (variant: CalloutVariant) => ReturnType
		}
	}
}

// A global attribute rather than a Blockquote subclass: several editors already
// extend Blockquote themselves, and this has to survive every one of them.
export const CalloutVariantExtension = Extension.create({
	name: "calloutVariant",

	addGlobalAttributes() {
		return [
			{
				types: ["blockquote"],
				attributes: {
					calloutVariant: {
						default: DEFAULT_CALLOUT_VARIANT,
						parseHTML: (element) =>
							readCalloutVariant(element.getAttribute("data-callout")),
						renderHTML: (attributes) => {
							const variant = readCalloutVariant(attributes.calloutVariant)
							// The default needs no attribute, so existing articles keep
							// their exact HTML and no diff appears on a resave.
							if (variant === DEFAULT_CALLOUT_VARIANT) return {}
							return { "data-callout": variant }
						},
					},
				},
			},
		]
	},

	addCommands() {
		return {
			setCalloutVariant:
				(variant: CalloutVariant) =>
				({ commands }) =>
					commands.updateAttributes("blockquote", { calloutVariant: variant }),
		}
	},
})
